import React from 'react';

const cx = (...a) => a.filter(Boolean).join(' ');

export function Card({ children, className }) {
  return (
    <section
      className={cx(
        'bg-white rounded-3xl ring-1 ring-line shadow-card p-5',
        className
      )}
    >
      {children}
    </section>
  );
}

export function DTSectionHead({ eyebrow, title, action, dense = false }) {
  return (
    <div
      className={cx(
        'flex items-end justify-between gap-4',
        dense ? 'mb-3' : 'mb-4'
      )}
    >
      <div>
        {eyebrow && (
          <p className="text-[10.5px] font-bold tracking-[0.18em] text-brand uppercase">
            {eyebrow}
          </p>
        )}
        <h2
          className={cx(
            'mt-1 font-extrabold tracking-tight text-ink-strong leading-tight',
            dense ? 'text-[16px]' : 'text-[20px]'
          )}
        >
          {title}
        </h2>
      </div>
      {action}
    </div>
  );
}

export function DTInfoTile({ icon, label, value, sub, tone = 'paper', className }) {
  const tones = {
    paper: 'bg-white ring-1 ring-line',
    ink: 'bg-ink-strong text-white',
    amber: 'bg-brand text-ink-strong',
    softer: 'bg-brand-softer ring-1 ring-amber-200',
  };
  const iconWrap =
    tone === 'ink'
      ? 'bg-white/10 text-white'
      : tone === 'amber'
        ? 'bg-ink-strong/10 text-ink-strong'
        : 'bg-paper text-gray-500';
  return (
    <div className={cx('rounded-2xl p-4 flex items-start gap-3', tones[tone], className)}>
      {icon && (
        <div
          className={cx(
            'w-9 h-9 rounded-xl grid place-items-center shrink-0',
            iconWrap
          )}
        >
          {icon}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div
          className={cx(
            'text-[10.5px] uppercase tracking-[0.12em] font-bold',
            tone === 'ink'
              ? 'text-white/60'
              : tone === 'amber'
                ? 'text-ink-strong/70'
                : 'text-gray-400'
          )}
        >
          {label}
        </div>
        <div
          className={cx(
            'mt-0.5 font-extrabold tracking-tight',
            tone === 'ink' ? 'text-white' : 'text-ink-strong'
          )}
        >
          {value}
        </div>
        {sub && (
          <div
            className={cx(
              'mt-0.5 text-[11px]',
              tone === 'ink' ? 'text-white/60' : 'text-gray-500'
            )}
          >
            {sub}
          </div>
        )}
      </div>
    </div>
  );
}

export function DTHeaderAction({ icon, label, primary = false, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        'inline-flex items-center gap-2 px-3.5 h-10 rounded-full text-[12.5px] font-bold transition',
        primary
          ? 'bg-ink-strong text-white hover:bg-black'
          : 'bg-white ring-1 ring-line text-ink-strong hover:bg-paper'
      )}
    >
      {icon}
      {label}
    </button>
  );
}

// `wrap` lets a long label run onto a second line instead of truncating — used
// where every amenity must be readable (the "Neste evento" modal).
export function DTAmenityChip({ label, checked, wrap = false }) {
  return (
    <div
      className={cx(
        'flex items-center gap-2 px-3 py-2.5 rounded-xl text-[12px] font-semibold leading-tight',
        checked
          ? 'bg-brand-softer ring-1 ring-amber-200 text-ink-strong'
          : 'bg-paper ring-1 ring-line text-gray-400'
      )}
    >
      <div
        className={cx(
          'w-4 h-4 rounded-full grid place-items-center shrink-0',
          checked
            ? 'bg-brand text-ink-strong'
            : 'bg-white ring-1 ring-line text-gray-300'
        )}
      >
        {checked && (
          <svg
            viewBox="0 0 24 24"
            width={10}
            height={10}
            fill="none"
            stroke="currentColor"
            strokeWidth={3}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}
      </div>
      <span
        className={wrap ? 'min-w-0 break-words' : 'truncate'}
        title={label}
      >
        {label}
      </span>
    </div>
  );
}

export function DTCapacityBar({ current, total, showCount = true }) {
  const safeTotal = Number.isFinite(total) && total > 0 ? total : 0;
  const safeCurrent = Number.isFinite(current) ? current : 0;
  const pct = safeTotal > 0 ? Math.min((safeCurrent / safeTotal) * 100, 100) : 0;
  let label = 'Spaces open';
  let dotCls = 'text-emerald-700';
  let barCls = 'bg-emerald-500';
  if (safeCurrent === 0) {
    label = 'Spaces open';
  } else if (pct >= 100) {
    label = 'Sold out';
    dotCls = 'text-rose-700';
    barCls = 'bg-rose-500';
  } else if (pct > 50) {
    label = 'Almost full';
    dotCls = 'text-amber-800';
    barCls = 'bg-amber-500';
  } else {
    label = 'Filling up';
  }
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span
          className={cx(
            'text-[10.5px] uppercase tracking-[0.12em] font-bold',
            dotCls
          )}
        >
          {label}
        </span>
        {showCount && (
          <span className="text-[11px] tabular-nums font-bold text-gray-700">
            <span className="text-ink-strong">{safeCurrent}</span>
            <span className="text-gray-400 font-medium">
              /{safeTotal} spaces
            </span>
          </span>
        )}
      </div>
      <div className="h-2 rounded-full bg-paper ring-1 ring-line overflow-hidden">
        <div
          className={cx('h-full rounded-full transition-all', barCls)}
          style={{ width: `${Math.max(pct, 4)}%` }}
        />
      </div>
    </div>
  );
}

export function DTStateBadge({
  daysToStart,
  sold,
  isLive,
  isCancelled,
  className,
}) {
  // A cancellation outranks every other state: the event is not sold out, not
  // live and not "in N days" any more — it is off (T2B-420).
  if (isCancelled) {
    return (
      <span
        className={cx(
          'inline-flex items-center px-2 py-1 rounded-full bg-rose-50 text-rose-700 ring-1 ring-rose-200 text-[10px] font-bold uppercase tracking-[0.08em]',
          className
        )}
      >
        Cancelled
      </span>
    );
  }
  if (sold) {
    return (
      <span
        className={cx(
          'inline-flex items-center px-2 py-1 rounded-full bg-rose-100 text-rose-700 ring-1 ring-rose-200 text-[10px] font-bold uppercase tracking-[0.08em]',
          className
        )}
      >
        Sold out
      </span>
    );
  }
  if (isLive) {
    return (
      <span
        className={cx(
          'inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 text-[10px] font-bold uppercase tracking-[0.08em]',
          className
        )}
      >
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
        Live
      </span>
    );
  }
  if (Number.isFinite(daysToStart) && daysToStart > 0 && daysToStart <= 14) {
    return (
      <span
        className={cx(
          'inline-flex items-center px-2 py-1 rounded-full bg-brand-soft text-amber-900 ring-1 ring-amber-200 text-[10px] font-bold uppercase tracking-[0.08em]',
          className
        )}
      >
        In {daysToStart}d
      </span>
    );
  }
  return null;
}

export function DTDatePill({ rangeLabel, days, variant = 'glass' }) {
  if (!rangeLabel) return null;
  const range =
    variant === 'glass'
      ? 'bg-white/95 backdrop-blur text-ink-strong'
      : 'bg-ink-strong text-white';
  const sub =
    variant === 'glass'
      ? 'bg-black/45 backdrop-blur text-white'
      : 'bg-paper text-gray-500';
  return (
    <div className="inline-flex items-center gap-1.5">
      <span
        className={cx(
          'inline-flex items-center px-2.5 py-1 rounded-full text-[10.5px] font-bold tracking-[0.04em] uppercase whitespace-nowrap shadow-sm',
          range
        )}
      >
        {rangeLabel}
      </span>
      {Number.isFinite(days) && days > 0 && (
        <span
          className={cx(
            'inline-flex items-center px-2 py-1 rounded-full text-[10.5px] font-semibold whitespace-nowrap',
            sub
          )}
        >
          {days}d
        </span>
      )}
    </div>
  );
}
